import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import {
  faPlus, faTrash, faFile, faPencil, faSearch, faPrint,
  faDownload, faCheck, faTimes, faCamera, faBan,
  faAngleLeft, faAngleUp, faAngleDown,
  faUser, faUsers, faSpinner, faBars,
  faListUl, faCar, faTruck, faWrench, faPlane,
  faCalendar, faChartLine, faCreditCard, faBuildingColumns,
  faBook, faBullhorn, faCog, faFileExcel, faFilePdf, faFileWord,
  faCheckSquare, faSquareCheck, faEnvelope, faHouse, faTriangleExclamation,
  faArrowsRotate, faEye, faEyeSlash, faExpand, faCompress,
  faRotateLeft, faRotateRight, faCalculator, faCodeFork,
  faCircleCheck, faHandPointDown, faHandPointRight, faSquareParking,
  faXmark,
  // Шаг 4e: иконки кнопок (icon="..."), ранее рендерившиеся через FA-шрифт.
  faArrowDown, faArrowUp, faScaleBalanced, faBarcode, faCalendarDay,
  faChevronLeft, faChevronRight, faChevronDown, faClipboard, faCopy, faGem, faFilter,
  faTableColumns,
  faHandPointer, faCircleInfo, faMap, faPaste, faRetweet,
  faRub, faRubleSign, faSave, faTag, faToggleOn, faUserPlus,
  faWandMagicSparkles, faWindowClose, faWarehouse,
  faGripVertical,
} from '@fortawesome/free-solid-svg-icons';
import { faCcVisa } from '@fortawesome/free-brands-svg-icons';
import { faSquare as faSquareRegular } from '@fortawesome/free-regular-svg-icons';

/**
 * Карта имён иконок (значение пропа `name` у `<Icon>`) на определения FA6.
 *
 * Имена в ключах оставлены в v4-нотации (kebab-case, суффиксы `-o`), чтобы
 * совпадать со значениями из конфигов: `icon="plus"` в кнопках и `icon: 'truck'`
 * в url.js. Значения — уже корректные FA6-иконки, поэтому «кривые» из-за
 * несовпадения v4↔v6 имён лечатся здесь же.
 *
 * Иконки отсутствуют в карте намеренно: новые добавляются по мере миграции.
 * Для неизвестных имён `<Icon>` отдаёт fallback на FA-шрифт (`fa fa-<name>`).
 */
export const iconMap: Record<string, IconDefinition> = {
  // Кнопки (топ-15)
  plus: faPlus,
  trash: faTrash,
  file: faFile,
  pencil: faPencil,
  search: faSearch,
  print: faPrint,
  download: faDownload,
  check: faCheck,
  times: faTimes,
  camera: faCamera,
  ban: faBan,
  'angle-left': faAngleLeft,
  user: faUser,
  users: faUsers,
  spinner: faSpinner,

  // v4 → v6: имена, переименованные между версиями FA
  'file-excel-o': faFileExcel,
  'file-pdf-o': faFilePdf,
  'file-word-o': faFileWord,
  'check-square': faCheckSquare,
  // Boolean-ячейки таблиц: заполненный с галочкой / пустой обведённый квадрат.
  // Пустой квадрат намеренно из regular-пакета (outline), как прежний `fa-regular fa-square`,
  // а не solid — иначе отрисовывался бы сплошной чёрный квадрат.
  'square-check': faSquareCheck,
  'square': faSquareRegular,
  'line-chart': faChartLine,
  'list-ul': faListUl,
  bank: faBuildingColumns,
  'angle-up': faAngleUp,
  'angle-down': faAngleDown,
  home: faHouse,
  'exclamation-triangle': faTriangleExclamation,
  refresh: faArrowsRotate,
  'eye': faEye,
  'eye-slash': faEyeSlash,
  expand: faExpand,
  compress: faCompress,
  undo: faRotateLeft,
  repeat: faRotateRight,
  envelope: faEnvelope,
  calculator: faCalculator,
  'code-fork': faCodeFork,
  'check-circle-o': faCircleCheck,
  'hand-o-down': faHandPointDown,
  'hand-o-right': faHandPointRight,
  parking: faSquareParking,
  close: faXmark,
  'cc-visa': faCcVisa,

  // Навигация (NAVI из src/lib/router/url.js)
  bars: faBars,
  car: faCar,
  truck: faTruck,
  wrench: faWrench,
  plane: faPlane,
  calendar: faCalendar,
  'credit-card': faCreditCard,
  book: faBook,
  bullhorn: faBullhorn,
  cog: faCog,

  // Шаг 4e: иконки кнопок (icon="...") и UI, ранее рендерившиеся через FA-шрифт.
  // Удаление FA SCSS (main.scss) требует, чтобы все реально используемые имена
  // были в карте — иначе fallback <i class="fa fa-..."> останется без шрифта.
  'arrow-down': faArrowDown,
  'arrow-up': faArrowUp,
  'balance-scale': faScaleBalanced,
  barcode: faBarcode,
  'calendar-day': faCalendarDay,
  'chevron-left': faChevronLeft,
  'chevron-right': faChevronRight,
  'chevron-down': faChevronDown,
  clipboard: faClipboard,
  columns: faTableColumns,
  copy: faCopy,
  diamond: faGem,
  filter: faFilter,
  'hand-pointer-o': faHandPointer,
  'info-circle': faCircleInfo,
  map: faMap,
  paste: faPaste,
  retweet: faRetweet,
  rub: faRub,
  'ruble-sign': faRubleSign,
  save: faSave,
  tag: faTag,
  'toggle-on': faToggleOn,
  'user-plus': faUserPlus,
  'wand-magic-sparkles': faWandMagicSparkles,
  'window-close': faWindowClose,
  // Маркер «на хранении» в списках отчёта «Аналитика хранения» (markers.ts)
  warehouse: faWarehouse,
  'grip-vertical': faGripVertical,
};
